import React, {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  useReactTable,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  SortingState,
  getSortedRowModel,
  Table,
  TableOptions,
  Row,
  ColumnResizeMode,
  Header,
  Updater,
  getGroupedRowModel,
  getExpandedRowModel,
  ExpandedState,
  ColumnSizingState,
  ColumnOrderState,
  VisibilityState
} from "@tanstack/react-table";
import { cn } from "@/utils/utils";
import { useTranslation } from "react-i18next";
import { MemoizedTableRow } from "./TableRow";
import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import { TableHeader } from "./TableHeader/TableHeader";
import { ChevronDown, ChevronRight } from "@dreamhub/designsystem/icons";
import Text from "@/components/ui/text";
import InlineLoader from "@/components/ui/inline-loader";
import { useInfiniteScrollTrigger } from "@/hooks/useInfiniteScrollTrigger";
import IconContainer from "@/components/ui/icon-container";
import Spinner from "@/components/ui/spinner";
import DataTableEmptyState from "./DataTableEmptyState";
import { ViewDataConfiguration } from "@/entities/views/types";
import {
  getLeadingLockedIds,
  getTempView,
  updateTempViewColumnsOrder
} from "@/entities/dataTables/helpers";
import { ColumnData } from "@/entities/dataTables/types";
import {
  GroupRankResolvers,
  orderRowsForGrouping,
  resolveGroupRank
} from "@/entities/dataTables/groupOrdering";
import {
  filePresenceGroupRank,
  HAS_FILE_GROUP_KEY,
  isFilePresenceGroupedColumn,
  toFilePresenceGroupKey
} from "@/entities/dataTables/fileGrouping";
import {
  columnCurrencyGroupKey,
  currencyGroupRank,
  getCurrencyGroupCode
} from "@/entities/dataTables/currencyGrouping";
import {
  columnDateBucketKey,
  dateBucketRank,
  formatDateBucketLabel,
  getColumnRawValue,
  getDateBucket
} from "@/entities/dataTables/monthGrouping";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { useSidebarState } from "@/services/context/SidebarContext";
import { isEmpty } from "lodash";
import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";
import { Filters, FiltersSetter } from "@/services/api/types";

// Merge a saved column order with the table's default order, inserting any
// columns absent from the saved order at their natural defaultOrder position
// (rather than appending them to the end). This keeps newly-added hardcoded
// columns in their declared position even when an older saved view doesn't
// reference them.
const mergeWithDefaultOrder = (
  savedOrder: string[],
  defaultOrder: string[]
): string[] => {
  const defaultSet = new Set(defaultOrder);
  const validOrder = savedOrder.filter((id) => defaultSet.has(id));
  const validSet = new Set(validOrder);
  const missing = defaultOrder.filter((id) => !validSet.has(id));
  if (missing.length === 0) return validOrder;

  // For each missing column, find the nearest saved column that follows it in
  // defaultOrder. We insert the missing column immediately before that anchor
  // in the merged output. If no such anchor exists (the missing column is
  // after all saved columns in defaultOrder), append at the end.
  const insertBefore = new Map<string, string[]>();
  const appendAtEnd: string[] = [];
  for (const missingColumnId of missing) {
    const missingColumnIndex = defaultOrder.indexOf(missingColumnId);
    let anchor: string | undefined;
    for (let i = missingColumnIndex + 1; i < defaultOrder.length; i++) {
      if (validSet.has(defaultOrder[i])) {
        anchor = defaultOrder[i];
        break;
      }
    }
    if (anchor === undefined) {
      appendAtEnd.push(missingColumnId);
    } else {
      const list = insertBefore.get(anchor) ?? [];
      list.push(missingColumnId);
      insertBefore.set(anchor, list);
    }
  }

  const merged: string[] = [];
  for (const id of validOrder) {
    const pre = insertBefore.get(id);
    if (pre) merged.push(...pre);
    merged.push(id);
  }
  merged.push(...appendAtEnd);
  return merged;
};

const getColumnDefId = <TData,>(
  columnDef: ColumnDef<TData, unknown>
): string | undefined => {
  if (typeof columnDef.id === "string") return columnDef.id;
  if ("accessorKey" in columnDef) return columnDef.accessorKey as string;
  return undefined;
};

const getLeafColumnIds = (table: Table<unknown> | null): Set<string> =>
  new Set(table?.getAllLeafColumns().map((col) => col.id) ?? []);

// Falls back to TanStack's own index-derived id, so a row carrying no id is no worse off than before.
const resolveRowId = (
  row: { id?: string } | null,
  index: number,
  parent?: Row<unknown>
): string => row?.id ?? `${parent ? [parent.id, index].join(".") : index}`;

/**
 * One group header the table will render. `row` is TanStack's grouped row when
 * rows for the group have been loaded; a declared group with nothing loaded yet
 * still gets an entry, so the group list is complete from the first paint.
 */
interface GroupEntry {
  key: string;
  label: React.ReactNode;
  row?: Row<Record<string, unknown>>;
}

interface DataTableProps {
  columns: ColumnDef<any, any>[];
  data: any[];
  initialSortBy?: SortingState;
  initialColumnVisibility?: Record<string, boolean>;
  tableOptions?: Partial<TableOptions<any>>;
  onRowClick?: (row: Row<any>) => void;
  headerClassName?: string;
  stickyLeftColumn?: boolean;
  resizableColumns?: boolean;
  height?: string;
  isInfiniteScroll?: boolean;
  hasMore?: boolean;
  isDataLoading?: boolean;
  isLoadingMore?: boolean; // For infinite scroll table
  onLoadMore?: () => void;
  loadingThreshold?: number; // Distance from bottom to trigger loading (in px)
  /**
   * Set this whenever the sort field is sent to the API, so the server owns the
   * ordering. Left off, TanStack re-sorts the page it was handed on the raw
   * accessor value and the server's ordering is discarded.
   */
  manualSorting?: boolean;
  onSortingChange?: (updater: Updater<SortingState>) => void;
  showRowHover?: (row: Row<any>) => boolean;
  getRowClassName?: (row: Row<any>) => string | undefined;
  firstColumnWidth?: number;
  dataTest?: string;
  groupBy?: string[];
  /**
   * Rank resolvers keyed by column id, for grouped columns that have an order of their own
   * (a stage's pipeline position, a status enum's ordinal). Without one, a grouped column's
   * order is whatever the API's sort field produced, which is only right while the user
   * happens to be sorting by that same column. Pass a stable reference.
   *
   * Requires `manualSorting`, as does grouping by a date-bucketed column, which ranks its
   * groups in calendar order. The reorder happens before the table sees the rows, so without
   * it TanStack's own sorted row model runs afterwards and an active sort on another column
   * takes the group order back.
   */
  groupRankResolvers?: GroupRankResolvers<any>;
  /** Label for the group of rows with no value. */
  emptyGroupLabel?: string;
  groupingConfig?: {
    typeLabel?: string;
    aggregationKey?: string;
    aggregationLabel?: string;
    formatAggregation?: (value: number) => string;
    subtotalAggregations?: Array<{
      key: string;
      label: string;
      aggregate: (rows: any[]) => number;
      /**
       * Same figure derived from a server-side group summary. Preferred over
       * `aggregate` whenever a summary exists for the group.
       */
      fromSums?: (
        sums: Record<string, number>,
        count: number,
        counts: Record<string, number>
      ) => number;
      format?: (value: number) => string;
    }>;
    /**
     * The groups this table has, declared up front. Without it the header set
     * is whatever grouping the loaded rows happen to produce, so groups appear
     * one at a time as infinite scroll pages — and a group with no rows loaded
     * yet is invisible even though the page already knows it exists.
     */
    groups?: Array<{ value: string | number; label: React.ReactNode }>;
    /**
     * Exact per-group figures keyed by the group's raw value. `subRows` only
     * covers the pages loaded so far, so on an infinite-scroll table the
     * client-side count and total climb as the user scrolls. A summary, where
     * one exists, is the whole group and wins.
     */
    groupSummaries?: Record<
      string,
      {
        total: number;
        sums: Record<string, number>;
        counts: Record<string, number>;
        isLoading: boolean;
      }
    >;
  };
  enableColumnReorder?: boolean;
  tableId?: string;
  selectedView?: ViewDataConfiguration;
  onColumnsChange?: (updatedColumns: ColumnData[]) => void;
  columnsState?: ColumnData[];
  selectedRows?: Set<string>;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
  onClearSearch?: () => void;
  // A failed fetch has no rows either; without this it reads as "No results".
  isError?: boolean;
  onRetry?: () => void;
  customEmptyState?: React.ReactNode;
  filterOptions?: DataTableFilter[];
  filters?: Filters;
  setFilters?: FiltersSetter;
  className?: string;
}

const DataTable: FC<DataTableProps> = ({
  columns,
  data,
  initialSortBy = undefined,
  initialColumnVisibility = undefined,
  emptyGroupLabel,
  tableOptions = undefined,
  manualSorting = false,
  onSortingChange = undefined,
  onRowClick = undefined,
  headerClassName,
  stickyLeftColumn = false,
  resizableColumns = false,
  height = "20rem",
  isInfiniteScroll = false,
  hasMore = false,
  isDataLoading = false,
  isLoadingMore = false,
  onLoadMore = undefined,
  loadingThreshold = 100,
  showRowHover = () => true,
  getRowClassName,
  firstColumnWidth = 300,
  dataTest = "table",
  groupBy = undefined,
  groupRankResolvers = undefined,
  groupingConfig = undefined,
  enableColumnReorder = false,
  selectedView,
  onColumnsChange,
  columnsState,
  selectedRows,
  hasActiveFilters = false,
  onClearFilters,
  onClearSearch,
  isError = false,
  onRetry,
  customEmptyState,
  filterOptions,
  filters,
  setFilters,
  className
}) => {
  const { t } = useTranslation();
  const emptyStateLabels = {
    title: t("tables.emptyState.title"),
    filtersDescription: t("tables.emptyState.filtersDescription"),
    searchDescription: t("tables.emptyState.searchDescription"),
    clearFilters: t("tables.emptyState.clearFilters"),
    clearSearch: t("tables.emptyState.clearSearch"),
    errorTitle: t("tables.emptyState.errorTitle"),
    errorDescription: t("tables.emptyState.errorDescription"),
    retry: t("tables.emptyState.retry")
  };
  const { enabled: columnFlexEnabled, loading: flexFlagLoading } =
    useFeatureFlag(FeatureFlags.CRM_7175_COLUMN_FLEX_PERSISTENCE);

  const [isReady, setIsReady] = useState(false);
  const [hoveredColumnId, setHoveredColumnId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([]);
  const initializedRef = useRef(false);
  const pendingScrollLeftRef = useRef<number | null>(null);

  const { isSidebarOpen } = useSidebarState();

  const defaultOrder = useMemo<string[]>(
    () =>
      columns
        .map(getColumnDefId)
        .filter((id): id is string => id !== undefined),
    [columns]
  );

  // A pinned table's entity column can't move, nor can any fixed utility column declared ahead of it.
  const lockedColumnIds = useMemo<string[]>(() => {
    if (!stickyLeftColumn) return [];
    const locked: string[] = [];
    for (const columnDef of columns) {
      const id = getColumnDefId(columnDef);
      if (id === undefined) continue;
      locked.push(id);
      const meta = columnDef.meta as { disableDragging?: boolean } | undefined;
      if (!meta?.disableDragging) break;
    }
    return locked;
  }, [columns, stickyLeftColumn]);

  useEffect(() => {
    if (!enableColumnReorder || !selectedView || selectedView?.id === "") {
      setColumnOrder(defaultOrder);
      return;
    }

    const tempView = getTempView(selectedView);

    if (tempView) {
      try {
        const tempViewColumnsState = tempView?.config?.columns;

        if (tempViewColumnsState && tempViewColumnsState.length > 0) {
          const tempViewOrder = tempViewColumnsState.map(
            (col: ColumnData) => col.id
          );
          setColumnOrder(mergeWithDefaultOrder(tempViewOrder, defaultOrder));
        } else {
          setColumnOrder(defaultOrder);
        }
      } catch (e) {
        console.error("Error parsing saved column order:", e);
        setColumnOrder(defaultOrder);
      }
    } else {
      setColumnOrder(defaultOrder);
    }
    initializedRef.current = true;
  }, [enableColumnReorder, defaultOrder, selectedView]);

  useEffect(() => {
    if (columnOrder.length > 0) {
      setIsReady(true);
    }
  }, [columnOrder]);

  const buildVisibilityState = useCallback(
    (columnDataList: ColumnData[]): VisibilityState => {
      const visibilityMap = new Map<string, boolean>(
        columnDataList.map((col) => [col.id, col.isVisible])
      );
      const visibilityState: VisibilityState = {};
      columns.forEach((col) => {
        const colId =
          typeof col.id === "string"
            ? col.id
            : "accessorKey" in col
              ? (col.accessorKey as string)
              : undefined;
        if (colId) {
          visibilityState[colId] = visibilityMap.get(colId) ?? false;
        }
      });
      return visibilityState;
    },
    [columns]
  );

  useEffect(() => {
    if (columnsState && columnsState.length > 0) {
      const newOrder = columnsState.map((col) => col.id);
      setColumnOrder(mergeWithDefaultOrder(newOrder, defaultOrder));
    }
  }, [columnsState, defaultOrder]);

  const columnVisibility = useMemo(() => {
    if (columnsState && columnsState.length > 0) {
      return buildVisibilityState(columnsState);
    }
    const tempView = getTempView(selectedView);
    const tempViewColumns = tempView?.config?.columns;
    if (tempViewColumns && tempViewColumns.length > 0) {
      return buildVisibilityState(tempViewColumns);
    }
    return initialColumnVisibility ?? {};
  }, [
    columnsState,
    selectedView,
    buildVisibilityState,
    initialColumnVisibility
  ]);

  // The lock holds only while those columns actually lead, in whatever order: a view saved with the first
  // column moved keeps its order, and that column stays movable until it is back in front.
  const activeLockedColumnIdSet = useMemo(() => {
    const groupedIds = new Set(groupBy ?? []);
    return getLeadingLockedIds(
      columnOrder,
      new Set(lockedColumnIds),
      (id) => columnVisibility[id] === false || groupedIds.has(id)
    );
  }, [columnOrder, columnVisibility, groupBy, lockedColumnIds]);

  // Grouping leaves the sort alone: the user's sort still orders the rows inside
  // each group, and a grouped column with a rank resolver orders the groups.
  useEffect(() => {
    if (groupBy && groupBy.length > 0) {
      setExpanded(true);
      if (containerRef.current) {
        containerRef.current.scrollTop = 0;
      }
    } else {
      setExpanded({});
    }
  }, [groupBy]);

  const containerRef = useRef<HTMLDivElement | null>(null);

  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});

  const tableRef = useRef<Table<unknown> | null>(null);
  const columnsStateRef = useRef(columnsState);
  columnsStateRef.current = columnsState;
  const selectedViewRef = useRef(selectedView);
  selectedViewRef.current = selectedView;
  const onColumnsChangeRef = useRef(onColumnsChange);
  onColumnsChangeRef.current = onColumnsChange;

  const flexSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Symbol sentinel: can never accidentally equal selectedView?.id when the prop is omitted.
  const seededViewIdRef = useRef<string | symbol | undefined>(
    Symbol("unseeded")
  );
  const userResizeRef = useRef(false);

  const modifiedColumns = useMemo(() => {
    return columns.map((column, index) => ({
      size: index === 0 ? firstColumnWidth : (column.size ?? 150),
      ...column,
      // After spread so column-level minSize wins; falls back to 100 globally.
      minSize: column.minSize ?? 100,
      ...(getDateBucket(column.meta)
        ? {
            getGroupingValue: (row: unknown) => columnDateBucketKey(column, row)
          }
        : {}),
      ...(isFilePresenceGroupedColumn(column.meta)
        ? {
            getGroupingValue: (row: unknown) =>
              toFilePresenceGroupKey(getColumnRawValue(column, row))
          }
        : {}),
      ...(getCurrencyGroupCode(column.meta)
        ? {
            getGroupingValue: (row: unknown) =>
              columnCurrencyGroupKey(column, row)
          }
        : {})
    }));
  }, [columns, firstColumnWidth]);

  const modifiedColumnsRef = useRef(modifiedColumns);
  modifiedColumnsRef.current = modifiedColumns;

  // TanStack clamps to 0, not minSize — enforce minSize here so stored state and render stay in sync.
  const handleColumnSizingChange = useCallback(
    (
      updater:
        | ColumnSizingState
        | ((prev: ColumnSizingState) => ColumnSizingState)
    ) => {
      userResizeRef.current = true;
      setColumnSizing((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        const clamped: ColumnSizingState = {};
        for (const [colId, size] of Object.entries(next)) {
          const colDef = modifiedColumnsRef.current.find((col) => {
            const entry = col as { accessorKey?: string; id?: string };
            return (entry.id ?? entry.accessorKey) === colId;
          });
          const minSize = colDef?.minSize ?? 100;
          clamped[colId] = Math.max(size, minSize);
        }
        return clamped;
      });
    },
    []
  );

  const defaultColumn = useMemo(
    () => ({
      sortingFn: "text" as const
    }),
    []
  );

  // Only an explicit descending sort on the grouped column flips its order; any other sort leaves it ascending.
  const isGroupSortDesc =
    (groupBy?.length === 1 &&
      initialSortBy?.find((sort) => sort.id === groupBy[0])?.desc) ??
    false;
  const singleGroupId = groupBy?.length === 1 ? groupBy[0] : undefined;
  const singleGroupedColumn = useMemo(
    () =>
      singleGroupId
        ? columns.find((column) => getColumnDefId(column) === singleGroupId)
        : undefined,
    [columns, singleGroupId]
  );
  const tableGroupRank = resolveGroupRank(groupBy, groupRankResolvers);
  // A table's own resolver wins; otherwise date buckets keep calendar order, file
  // groups put rows with a file first and currency groups put rows with no amount last.
  const resolvedGroupRank = useMemo(() => {
    if (tableGroupRank) return tableGroupRank;
    if (!singleGroupedColumn) return undefined;
    const dateBucket = getDateBucket(singleGroupedColumn.meta);
    if (dateBucket)
      return (row: unknown) =>
        dateBucketRank(
          columnDateBucketKey(singleGroupedColumn, row),
          dateBucket
        );
    if (isFilePresenceGroupedColumn(singleGroupedColumn.meta))
      return filePresenceGroupRank(singleGroupedColumn);
    if (getCurrencyGroupCode(singleGroupedColumn.meta))
      return currencyGroupRank(singleGroupedColumn);
    return undefined;
  }, [tableGroupRank, singleGroupedColumn]);

  // Reordering moves rows, and an index-derived key remounts every displaced row on each page load.
  // Defaulted here rather than per table so a table that wires a resolver cannot forget it; tableOptions still overrides.
  const getRowId =
    groupRankResolvers || resolvedGroupRank ? resolveRowId : undefined;

  // Without manualSorting the sorted row model runs after this reorder and undoes it, so fail loudly in dev.
  if (
    import.meta.env.DEV &&
    (groupRankResolvers || resolvedGroupRank) &&
    !manualSorting
  ) {
    console.warn(
      "DataTable: ranked grouping requires manualSorting — the grouped column's order will be overwritten whenever another column is sorted."
    );
  }

  const orderedData = useMemo(
    () => orderRowsForGrouping(data, resolvedGroupRank, isGroupSortDesc),
    [data, resolvedGroupRank, isGroupSortDesc]
  );

  const memoizedTableOptions = useMemo(
    () => ({
      data: orderedData,
      columns: modifiedColumns,
      defaultColumn,
      state: {
        columnSizing,
        columnVisibility,
        expanded,
        grouping: groupBy || [],
        sorting: initialSortBy,
        columnOrder
      },
      columnResizeMode: "onChange" as ColumnResizeMode,
      onColumnOrderChange: setColumnOrder,
      enableColumnOrdering: true,
      onColumnSizingChange: handleColumnSizingChange,
      onExpandedChange: setExpanded,
      getExpandedRowModel: getExpandedRowModel(),
      getGroupedRowModel: getGroupedRowModel(),
      autoResetExpanded: false,
      enableExpanding: true,
      manualSorting,
      getCoreRowModel: getCoreRowModel(),
      getRowId,
      onSortingChange,
      getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
      enableSortingRemoval: false,
      enableMultiSort: false,
      enableColumnResizing: resizableColumns,
      sortDescFirst: false,
      ...tableOptions
    }),
    [
      orderedData,
      modifiedColumns,
      defaultColumn,
      columnSizing,
      columnVisibility,
      expanded,
      groupBy,
      initialSortBy,
      manualSorting,
      getRowId,
      onSortingChange,
      resizableColumns,
      tableOptions,
      columnOrder,
      enableColumnReorder,
      handleColumnSizingChange
    ]
  );

  const table = useReactTable(memoizedTableOptions);
  tableRef.current = table;

  useLayoutEffect(() => {
    if (!columnFlexEnabled) return;
    if (!isReady || !containerRef.current) return;
    if (!columnsState?.length) return;
    if (seededViewIdRef.current === selectedView?.id) return;

    const containerWidth = containerRef.current.clientWidth;
    if (!containerWidth) return; // Container hidden (e.g. collapsed tab) — retry on next render.

    // getAllLeafColumns avoids TanStack's "column does not exist" warning from getColumn on unknown IDs.
    const existingIds = getLeafColumnIds(tableRef.current);
    const sizing: ColumnSizingState = {};
    for (const col of columnsState) {
      if (
        col.isVisible &&
        col.flex != null &&
        Number.isFinite(col.flex) &&
        col.flex > 0 &&
        existingIds.has(col.id)
      ) {
        sizing[col.id] = col.flex * containerWidth;
      }
    }
    if (Object.keys(sizing).length) {
      // Stamp only after confirmed write so a columnsState without flex values doesn't block a later seed.
      seededViewIdRef.current = selectedView?.id;
      setColumnSizing(sizing);
    }
  }, [isReady, columnsState, selectedView?.id, columnFlexEnabled]); // eslint-disable-line react-hooks/exhaustive-deps -- containerRef and tableRef are stable refs, not reactive values

  // Debounced flex capture after user drag; userResizeRef prevents seed-triggered sizing from writing back.
  useEffect(() => {
    if (
      !columnFlexEnabled ||
      !resizableColumns ||
      !Object.keys(columnSizing).length
    )
      return;

    if (flexSaveTimerRef.current) clearTimeout(flexSaveTimerRef.current);

    flexSaveTimerRef.current = setTimeout(() => {
      if (!userResizeRef.current) return;
      userResizeRef.current = false;

      const currentTable = tableRef.current;
      const currentColumnsState = columnsStateRef.current;
      const currentOnColumnsChange = onColumnsChangeRef.current;

      if (!currentTable || !currentOnColumnsChange) return;

      const containerWidth = containerRef.current?.clientWidth;
      if (!containerWidth) return;

      // No columnsState yet (e.g. embedded tables): synthesize from live columns so flex still persists.
      // Include hidden columns so onColumnsChange always receives a consistent "all columns" shape;
      // their flex assignment is skipped below by the getIsVisible() guard, matching the normal path.
      const baseColumns: ColumnData[] = currentColumnsState?.length
        ? currentColumnsState
        : currentTable.getAllLeafColumns().map((col, idx) => ({
            id: col.id,
            label:
              typeof col.columnDef.header === "string"
                ? col.columnDef.header
                : col.id,
            order: idx,
            isVisible: col.getIsVisible()
          }));

      if (!baseColumns.length) return;

      const existingIds = getLeafColumnIds(currentTable);
      const updatedColumns = baseColumns.map((colData) => {
        if (!existingIds.has(colData.id)) return colData;
        const col = currentTable.getColumn(colData.id);
        if (!col?.getIsVisible()) return colData;
        return { ...colData, flex: col.getSize() / containerWidth };
      });

      currentOnColumnsChange(updatedColumns);
    }, 150);

    return () => {
      if (flexSaveTimerRef.current) clearTimeout(flexSaveTimerRef.current);
    };
  }, [columnSizing, resizableColumns, columnFlexEnabled]); // eslint-disable-line react-hooks/exhaustive-deps -- columnsState, onColumnsChange, containerRef captured via stable refs

  // A saved view can still name a column the table no longer has. TanStack skips that grouping,
  // so the rows arrive ungrouped and must render flat rather than as zero group sections.
  const groupedColumnId =
    groupBy?.[0] && getLeafColumnIds(table).has(groupBy[0])
      ? groupBy[0]
      : undefined;
  const knownGroups = groupedColumnId ? groupingConfig?.groups : undefined;
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  useEffect(() => {
    setExpandedGroups(new Set());
  }, [groupedColumnId]);

  // Every group is on screen from the start and carries its own totals, so
  // with all of them collapsed there is nothing for a fetched row to show.
  // Paging on regardless is what leaves a spinner running under a table the
  // user is already finished reading.
  const skipLoadMore = !!knownGroups?.length && expandedGroups.size === 0;

  const { rootRef: scrollRootRef, sentinelRef: loadMoreSentinelRef } =
    useInfiniteScrollTrigger({
      enabled: isInfiniteScroll,
      canLoadMore:
        !!hasMore &&
        !isLoadingMore &&
        !skipLoadMore &&
        !isError &&
        !!onLoadMore,
      onLoadMore: () => onLoadMore?.(),
      threshold: loadingThreshold,
      itemCount: data.length
    });

  const setContainerRef = useCallback(
    (element: HTMLDivElement | null) => {
      containerRef.current = element;
      scrollRootRef(element);
    },
    [scrollRootRef]
  );

  const rows = table.getRowModel().rows;
  // With a missing first grouping id, TanStack still groups by the ids it does know,
  // so the flat list shows the rows underneath those groups. `getLeafRows` also returns
  // any nested group rows, which are not data rows.
  const flatRows =
    groupedColumnId || !rows.some((row) => row.getIsGrouped())
      ? rows
      : rows
          .filter((row) => row.getIsGrouped() && !row.parentId)
          .flatMap((row) =>
            row.getLeafRows().filter((leaf) => !leaf.getIsGrouped())
          );

  // A custom empty state replaces the table outright — it means "nothing here
  // yet", so the column headers and horizontal scroll would be noise. The
  // built-in filters/search states keep the table, since the columns are still
  // the context for what was searched.
  const showCustomEmptyState =
    isEmpty(data) &&
    !(isDataLoading || isLoadingMore) &&
    !isError &&
    !!customEmptyState;

  const visibleColumns = table
    .getHeaderGroups()[0]
    .headers.filter(
      (header) =>
        header.column.getIsVisible() &&
        !(groupBy || []).includes(header.column.id)
    );

  const getGroupSummary = (groupKey: string) =>
    groupingConfig?.groupSummaries?.[groupKey];

  /**
   * A group is still filling while its server figures are in flight, or while
   * infinite scroll has rows of it left to page in — the group's own count says
   * how many, and without a summary any remaining page could belong to it.
   */
  const isGroupPending = (
    summary: { isLoading: boolean; total: number } | undefined,
    loadedRowCount: number
  ) => {
    if (summary?.isLoading) return true;
    if (!(hasMore || isLoadingMore)) return false;
    return summary ? summary.total > loadedRowCount : true;
  };

  const renderSubtotalRow = (entry: GroupEntry) => {
    if (!groupingConfig?.subtotalAggregations) return null;

    const groupedRow = entry.row;
    const subRows = groupedRow?.subRows ?? [];
    const summary = getGroupSummary(entry.key);
    const count = summary?.total ?? subRows.length;
    const aggregations = groupingConfig.subtotalAggregations.map((agg) => ({
      ...agg,
      value:
        summary && agg.fromSums
          ? agg.fromSums(summary.sums, summary.total, summary.counts)
          : agg.aggregate(subRows.map((r) => r.original))
    }));

    return (
      // Below the pinned group-header rows (z-20), so it scrolls under them.
      <tr key={`${entry.key}-subtotal`} className="sticky left-0 right-0 z-10">
        <td colSpan={visibleColumns.length} className="py-2 px-0">
          <div
            className={cn(
              "flex items-center justify-end gap-8 text-base",
              "w-screen px-10 sticky  left-0 right-0 z-20 border-b border-border-border2 pb-2 mb-2",
              isSidebarOpen ? "w-[calc(100vw-208px)]" : "w-[calc(100vw-52px)]"
            )}
          >
            <div className="flex items-center gap-2">
              <span className="text-secondary">
                {groupingConfig?.typeLabel}
              </span>
              <span className="font-medium">{count}</span>
            </div>
            {aggregations.map((agg) => (
              <div key={agg.key} className="flex items-center gap-2">
                <span className="text-secondary">{agg.label}</span>
                <span className="font-medium">
                  {agg.format ? agg.format(agg.value) : agg.value}
                </span>
              </div>
            ))}
          </div>
        </td>
      </tr>
    );
  };

  const handleColumnHover = (columnId: string | null) => {
    setHoveredColumnId(columnId);
  };

  const renderResize = (
    header: Header<any, unknown>,
    isHeaderRow: boolean = false,
    isStickyLeftColumn: boolean = false
  ) => {
    // A fixed-width utility column (e.g. a bulk-select checkbox) in the sticky slot or the locked run has nothing to resize.
    const isPinnedOrLocked =
      isStickyLeftColumn || activeLockedColumnIdSet.has(header.column.id);
    if (isPinnedOrLocked && !header.column.getCanResize()) return null;
    return (
      <div
        onMouseDown={header.getResizeHandler()}
        onTouchStart={header.getResizeHandler()}
        role="separator"
        aria-orientation="vertical"
        aria-label={`Resize ${header.column.id} column`}
        tabIndex={0}
        className={cn(
          "absolute top-0 right-0 h-[105%] w-0.5 bg-transparent cursor-col-resize select-none touch-none transition-colors",
          isHeaderRow ? "right-[-1px]" : "right-0",
          hoveredColumnId === header.id ? "bg-border-border1" : "",
          header.column.getIsResizing() ? "bg-cta" : "",
          // Lifts the handle above the sticky header's separator line, which sits at the same edge.
          isStickyLeftColumn ? "z-40" : ""
        )}
      />
    );
  };

  const getGroupDisplayValue = useCallback(
    (row: Row<any>) => {
      const groupedColumnId = groupBy?.[0];
      if (!groupedColumnId) return "";

      const cell = row
        .getAllCells()
        .find((cell) => cell.column.id === groupedColumnId);
      if (!cell) return "";

      const dateBucket = getDateBucket(cell.column.columnDef.meta);
      if (dateBucket) {
        const bucketKey = String(row.getGroupingValue(groupedColumnId) ?? "");
        return bucketKey
          ? formatDateBucketLabel(bucketKey, dateBucket)
          : (emptyGroupLabel ?? t("blank"));
      }

      if (isFilePresenceGroupedColumn(cell.column.columnDef.meta)) {
        return row.getGroupingValue(groupedColumnId) === HAS_FILE_GROUP_KEY
          ? t("tables.hasFile")
          : t("tables.noFile");
      }

      // The group holds every amount in one currency, so it is labelled by the code
      // rather than by its first row's figure.
      if (getCurrencyGroupCode(cell.column.columnDef.meta)) {
        const currencyCode = String(row.getGroupingValue(groupedColumnId) ?? "");
        return currencyCode || (emptyGroupLabel ?? t("blank"));
      }

      const rawValue = cell.getValue() ?? "";
      return rawValue === ""
        ? (emptyGroupLabel ?? t("blank"))
        : flexRender(cell.column.columnDef.cell, cell.getContext());
    },
    [groupBy, t, emptyGroupLabel]
  );

  const groupEntries: GroupEntry[] = useMemo(() => {
    if (!groupedColumnId) return [];

    const loadedRows = new Map<string, Row<Record<string, unknown>>>();
    rows.forEach((row) => {
      if (!row.getIsGrouped()) return;
      loadedRows.set(String(row.getGroupingValue(groupedColumnId) ?? ""), row);
    });

    if (knownGroups?.length) {
      const declared = knownGroups.map((group) => {
        const key = String(group.value ?? "");
        return { key, label: group.label, row: loadedRows.get(key) };
      });
      // A loaded row whose key no declared group claims still belongs on screen.
      // Dropping it would hide records the row count and the KPI bar both count.
      const declaredKeys = new Set(declared.map((entry) => entry.key));
      const undeclared = Array.from(loadedRows.entries())
        .filter(([key]) => !declaredKeys.has(key))
        .map(([key, row]) => ({ key, label: getGroupDisplayValue(row), row }));
      return [...declared, ...undeclared];
    }

    return Array.from(loadedRows.entries()).map(([key, row]) => ({
      key,
      label: getGroupDisplayValue(row),
      row
    }));
  }, [rows, groupedColumnId, knownGroups, getGroupDisplayValue]);

  // A declared group has no TanStack row to hold expansion state until its rows
  // arrive, so expansion is tracked here whenever the page declares its groups.
  const isGroupExpanded = (entry: GroupEntry) =>
    knownGroups?.length
      ? expandedGroups.has(entry.key)
      : !!entry.row?.getIsExpanded();

  const toggleGroup = (entry: GroupEntry) => {
    if (!knownGroups?.length) {
      entry.row?.getToggleExpandedHandler()();
      return;
    }
    setExpandedGroups((previous) => {
      const next = new Set(previous);
      if (next.has(entry.key)) next.delete(entry.key);
      else next.add(entry.key);
      return next;
    });
    entry.row?.toggleExpanded(!entry.row.getIsExpanded());
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) return;

    pendingScrollLeftRef.current = containerRef.current?.scrollLeft ?? null;

    const groupedIds = new Set(groupBy ?? []);
    const visibleColumnIds = columnOrder.filter((id) => {
      const column = table.getColumn(id);
      return column?.getIsVisible() && !groupedIds.has(id);
    });

    const movedId = visibleColumnIds[result.source.index];
    if (!movedId || activeLockedColumnIdSet.has(movedId)) return;
    // Nothing may be dropped in front of, or between, the locked leading columns.
    const lockedVisibleCount = visibleColumnIds.filter((id) =>
      activeLockedColumnIdSet.has(id)
    ).length;
    if (result.destination.index < lockedVisibleCount) return;

    const reorderedVisible = [...visibleColumnIds];
    reorderedVisible.splice(result.source.index, 1);
    reorderedVisible.splice(result.destination.index, 0, movedId);

    let visibleCursor = 0;
    const newColumnOrder = columnOrder.map((id) => {
      const column = table.getColumn(id);
      if (groupedIds.has(id) || !column?.getIsVisible()) return id;
      return reorderedVisible[visibleCursor++] ?? id;
    });

    setColumnOrder(newColumnOrder);
    const updatedColumns = updateTempViewColumnsOrder(
      newColumnOrder,
      table.getState().columnVisibility,
      selectedView
    );

    if (updatedColumns && onColumnsChange) {
      onColumnsChange(updatedColumns);
    }
  };

  // Restore scroll position after column reorder across all cascading re-renders
  useLayoutEffect(() => {
    if (pendingScrollLeftRef.current !== null && containerRef.current) {
      containerRef.current.scrollLeft = pendingScrollLeftRef.current;
    }
  });

  // Clear the pending scroll position after paint settles
  useEffect(() => {
    if (pendingScrollLeftRef.current !== null) {
      const frame = requestAnimationFrame(() => {
        pendingScrollLeftRef.current = null;
      });
      return () => cancelAnimationFrame(frame);
    }
  });

  const showTableLoader = isDataLoading && !data.length;
  const showRefetchLoader = isDataLoading && data.length > 0;

  const containerStyle = className ? undefined : { height };

  // Hold the spinner until the flag is known when there are flex values to seed.
  // Prevents a flash of unsized columns before the seed useLayoutEffect can fire.
  const hasPendingFlexSeed =
    flexFlagLoading &&
    columnsState?.some((col) => col.flex != null && col.flex > 0);

  if (!isReady || hasPendingFlexSeed) {
    return (
      <div
        className={cn("overflow-auto relative", className)}
        style={containerStyle}
      >
        <div className="w-full h-full flex justify-center items-center">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  // Isolated so sticky headers and columns never paint over a pinned page header.
  return (
    <div
      ref={setContainerRef}
      className={cn("overflow-auto relative isolate", className)}
      style={containerStyle}
    >
      <DragDropContext onDragEnd={onDragEnd}>
        <table
          data-test={dataTest}
          className={cn(
            "min-w-full rounded-lg select-none",
            showCustomEmptyState ? "hidden" : ""
          )}
          style={{ width: table.getCenterTotalSize() }}
        >
          <thead className="sticky top-[-2px] z-20">
            {table.getHeaderGroups().map((headerGroup) => (
              <Droppable
                droppableId={headerGroup.id}
                direction="horizontal"
                key={headerGroup.id}
              >
                {(provided) => (
                  <tr
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="px-4"
                  >
                    {(() => {
                      let visibleIndex = 0;
                      return headerGroup.headers.map((header) => {
                        if (
                          !header.column.getIsVisible() ||
                          (groupBy || []).includes(header.column.id)
                        ) {
                          return null;
                        }
                        const idx = visibleIndex++;

                        return (
                          <TableHeader
                            key={header.id}
                            header={header}
                            index={idx}
                            enableColumnReorder={enableColumnReorder}
                            stickyLeftColumn={stickyLeftColumn}
                            isPositionLocked={activeLockedColumnIdSet.has(
                              header.column.id
                            )}
                            resizableColumns={resizableColumns}
                            currentGroupBy={groupBy || []}
                            renderResize={renderResize}
                            hoveredColumnId={hoveredColumnId}
                            handleColumnHover={handleColumnHover}
                            className={headerClassName}
                            filterOptions={filterOptions}
                            filters={filters}
                            setFilters={setFilters}
                          />
                        );
                      });
                    })()}
                    {provided.placeholder}
                  </tr>
                )}
              </Droppable>
            ))}
          </thead>
          <tbody>
            {groupedColumnId
              ? groupEntries.map((entry) => {
                  const summary = getGroupSummary(entry.key);
                  const expanded = isGroupExpanded(entry);
                  const subRows = entry.row?.subRows ?? [];
                  const pending = isGroupPending(summary, subRows.length);
                  return (
                    <React.Fragment key={entry.key}>
                      <tr className="group sticky w-screen bg-surface-elevation3 top-10 z-20">
                        <td
                          colSpan={visibleColumns.length}
                          className="px-4 py-[10px] border-t border-border-border2 select-none w-full"
                        >
                          <div
                            className={cn(
                              "w-screen flex gap-2 justify-between sticky pr-16 left-0 z-20",
                              isSidebarOpen
                                ? "w-[calc(100vw-208px)]"
                                : "w-[calc(100vw-52px)]"
                            )}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleGroup(entry);
                              }}
                              className="flex items-center gap-2"
                            >
                              <IconContainer size="md" color="primary">
                                {expanded ? <ChevronDown /> : <ChevronRight />}
                              </IconContainer>
                              <Text variant="lgLabel">{entry.label}</Text>
                            </button>
                            <div className="flex flex-row items-center gap-[20px]">
                              <div className="w-[45%] text-end">
                                <Text as="span" variant="lgLabel">
                                  {summary?.total ?? subRows.length}
                                </Text>
                                <Text
                                  as="span"
                                  variant="smBodySecondary"
                                  className="align-baseline ml-[6px]"
                                >
                                  {groupingConfig?.typeLabel}
                                </Text>
                              </div>
                              {groupingConfig?.aggregationKey && (
                                <div className="w-[55%] text-end">
                                  <Text as="span" variant="lgLabel">
                                    {groupingConfig.formatAggregation?.(
                                      summary
                                        ? summary.sums[
                                            groupingConfig.aggregationKey
                                          ] || 0
                                        : subRows.reduce(
                                            (sum, subRow) =>
                                              sum +
                                              ((subRow.original[
                                                groupingConfig.aggregationKey!
                                              ] as number) || 0),
                                            0
                                          )
                                    )}
                                  </Text>
                                  {groupingConfig.aggregationLabel && (
                                    <Text
                                      as="span"
                                      variant="smBodySecondary"
                                      className="align-baseline ml-[6px]"
                                    >
                                      {groupingConfig.aggregationLabel}
                                    </Text>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                      {expanded && (
                        <>
                          {subRows.map((subRow) => (
                            <MemoizedTableRow
                              key={subRow.id}
                              row={subRow}
                              onRowClick={onRowClick}
                              groupBy={groupBy || []}
                              showRowHover={showRowHover}
                              stickyLeftColumn={stickyLeftColumn}
                              columnOrder={columnOrder}
                              selectedRows={selectedRows}
                              getRowClassName={getRowClassName}
                            />
                          ))}
                          {pending && (
                            <tr key={`${entry.key}-loading`}>
                              <td
                                colSpan={visibleColumns.length}
                                className="p-0"
                              >
                                {/* The same inline loader the ungrouped list
                                    shows at its end, so a group whose rows are
                                    still paging in says so where the rows are
                                    rather than beside its figures. It centres
                                    on the visible width like the group header
                                    above it, not on a table the user has to
                                    scroll sideways to see the middle of. */}
                                <div
                                  className={cn(
                                    "sticky left-0 z-10",
                                    isSidebarOpen
                                      ? "w-[calc(100vw-208px)]"
                                      : "w-[calc(100vw-52px)]"
                                  )}
                                >
                                  <InlineLoader label={t("loading")} />
                                </div>
                              </td>
                            </tr>
                          )}
                          {renderSubtotalRow(entry)}
                        </>
                      )}
                    </React.Fragment>
                  );
                })
              : flatRows.map((row) => {
                  if (row.parentId && !row.getParentRow()?.getIsGrouped()) {
                    return null;
                  }

                  return (
                    <MemoizedTableRow
                      key={row.id}
                      row={row}
                      onRowClick={onRowClick}
                      groupBy={groupBy || []}
                      showRowHover={showRowHover}
                      stickyLeftColumn={stickyLeftColumn}
                      columnOrder={columnOrder}
                      selectedRows={selectedRows}
                      getRowClassName={getRowClassName}
                    />
                  );
                })}
          </tbody>
        </table>
        {showTableLoader && (
          <div className="w-full h-full flex justify-center items-center">
            <Spinner size="lg" />
          </div>
        )}
        {showRefetchLoader && (
          <div className="absolute inset-0 bg-white/50 backdrop-blur-sm flex justify-center items-center z-30">
            <Spinner size="lg" />
          </div>
        )}
        {isInfiniteScroll && !showCustomEmptyState && (
          <div ref={loadMoreSentinelRef} className="w-full py-4 text-center">
            {isLoadingMore && (
              <InlineLoader label={t("loading")} className="z-30" />
            )}
          </div>
        )}
        {isEmpty(data) && !(isDataLoading || isLoadingMore) ? (
          isError ? (
            <DataTableEmptyState
              view="error"
              labels={emptyStateLabels}
              onRetry={onRetry}
            />
          ) : showCustomEmptyState ? (
            <div className="min-h-full w-full flex items-center justify-center">
              {customEmptyState}
            </div>
          ) : hasActiveFilters ? (
            <DataTableEmptyState
              view="filters"
              labels={emptyStateLabels}
              onClearFilters={onClearFilters}
            />
          ) : (
            <DataTableEmptyState
              view="search"
              labels={emptyStateLabels}
              onClearSearch={onClearSearch}
            />
          )
        ) : null}
      </DragDropContext>
    </div>
  );
};

export default React.memo(DataTable);
